import React from 'react';

import {assert} from 'Asserts';
import {
  TransferStructuredTableDestinationType,
  TransferStructuredTableMetadata,
  TransferStructuredTableRowMetadata,
} from 'notebook/integrations/samples/TransferStructuredTableAdapter';
import unsupportedOverflowMenuItem from 'notebook/structured-tables/action-functions/unsupportedOverflowMenuItem';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {BaseTypedValueType, Cells, TableColumnType} from 'notebook/tables/TableConstants';
import {fillNextEmptyPositions} from 'notebook/transfer-tables/FillNextAvailablePositionsUtil';
import {TransferTableColumnGroup} from 'notebook/transfer-tables/TransferTableConstants';
import {getPositionStringsForRow} from 'notebook/transfer-tables/TransferTableEditableRowsUtil';
import {getEmptyPositionsByGridId, OrderingStrategy} from 'samples/grids/GridPositionUtil';
import * as StorableActions from 'samples/StorableActions';
import StorableStore from 'samples/StorableStore';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import APIUtil from 'util/APIUtil';
import {withLoadingScreen} from 'util/Promises';

import {ColumnDefinition, getColumnDefinitionIndex} from '../columns/ColumnDefinitions';
import {ActionFunctionParameters, StructuredTable, StructuredTableAdapter} from '../StructuredTableConstants';
import makeCustomButtonActionFunction from './makeCustomButtonActionFunction';

interface ButtonStatus {
  tooltipTitle: string;
  disabled: boolean;
}

function getDestinationBoxOrPlateColumnIndex({
  structuredTable,
  structuredTableAdapter,
}: {
  structuredTable: StructuredTable<TransferStructuredTableMetadata, TransferStructuredTableRowMetadata>;
  structuredTableAdapter: StructuredTableAdapter<
    TransferStructuredTableMetadata,
    TransferStructuredTableRowMetadata
  >;
}): number {
  const columnConstraints = structuredTableAdapter.toTable(structuredTable).columnConstraints;
  const columnDefinitions = columnConstraints.map((constraint) => constraint?.columnDefinition);
  let columnDefinitionType;
  switch (structuredTable.metadata.destinationType) {
    case TransferStructuredTableDestinationType.BOX:
      columnDefinitionType = TableColumnType.BOX;
      break;
    case TransferStructuredTableDestinationType.MATRIX_PLATE:
    case TransferStructuredTableDestinationType.PLATE:
      columnDefinitionType = TableColumnType.PLATE;
      break;
    default:
      throw new Error('Unexpected Destination Type for Transfer Table');
  }

  return getColumnDefinitionIndex({
    columnGroup: TransferTableColumnGroup.TARGET,
    columnDefinitionType,
    tableColumnDefinitions: columnDefinitions,
  });
}
/**
 * Fillable Grids are Destination grids from the rows in a TransferStructuredTable that:
 *   - have not been finalized,
 *   - match the destination type of the transfer table,
 *   - and have an empty Destination Position (or Destination Well) cell
 */
export function getGridIdsToFill({
  structuredTable,
  structuredTableAdapter,
  computedTable,
}: {
  structuredTable: StructuredTable<TransferStructuredTableMetadata, TransferStructuredTableRowMetadata>;
  structuredTableAdapter: StructuredTableAdapter<
    TransferStructuredTableMetadata,
    TransferStructuredTableRowMetadata
  >;
  computedTable: Array<Array<EvaluatedCell>>;
}): Array<string> {
  const columnConstraints = structuredTableAdapter.toTable(structuredTable).columnConstraints;
  const columnDefinitions: Array<ColumnDefinition> = columnConstraints
    .map((constraint) => constraint?.columnDefinition ?? null)
    .flatMap((value) => (value != null ? [value] : []));
  const destinationColumnIdx = getDestinationBoxOrPlateColumnIndex({structuredTable, structuredTableAdapter});
  return structuredTable.rows
    .map((row, rowIdx) => {
      if (!row.isFinalized) {
        const existingPositionStrings = getPositionStringsForRow(computedTable[rowIdx], columnDefinitions);
        if (existingPositionStrings.length !== 0) {
          return null;
        }
        const value = computedTable[rowIdx][destinationColumnIdx].value ?? null;
        if (value == null || value.type !== BaseTypedValueType.STORABLES) {
          return null;
        }

        const gridId = value.storableSummaries[0]?.id ?? null;
        if (gridId) {
          if (
            (structuredTable.metadata.destinationType === TransferStructuredTableDestinationType.BOX &&
              APIUtil.isBox(gridId)) ||
            ([
              TransferStructuredTableDestinationType.MATRIX_PLATE,
              TransferStructuredTableDestinationType.PLATE,
            ].includes(structuredTable.metadata.destinationType) &&
              APIUtil.isPlate(gridId))
          ) {
            return gridId;
          }
        }
      }
      return null;
    })
    .flatMap((gridId) => (gridId != null ? [gridId] : []));
}

const getTooltipText = ({
  disabled,
  destinationType,
}: {
  disabled: boolean;
  destinationType: TransferStructuredTableDestinationType;
}): string => {
  assert(
    destinationType !== TransferStructuredTableDestinationType.CONTAINER,
    'Destination type should be Plate or Box'
  );
  const destinationToPlural = {
    [TransferStructuredTableDestinationType.BOX]: 'boxes',
    [TransferStructuredTableDestinationType.MATRIX_PLATE]: 'plates',
    [TransferStructuredTableDestinationType.PLATE]: 'plates',
  };
  if (disabled) {
    return `No ${destinationToPlural[destinationType]} to fill`;
  }
  return 'Fill next available positions';
};

export async function handleFillNextAvailablePositions({
  structuredTable,
  structuredTableAdapter,
  computedTable,
  orderingStrategy,
  gridIdsToFill,
  updateHandler,
}: {
  structuredTable: StructuredTable<TransferStructuredTableMetadata, TransferStructuredTableRowMetadata>;
  structuredTableAdapter: StructuredTableAdapter<
    TransferStructuredTableMetadata,
    TransferStructuredTableRowMetadata
  >;
  computedTable: Array<Array<EvaluatedCell>>;
  orderingStrategy: OrderingStrategy;
  gridIdsToFill: Array<string>;
  updateHandler: ({updatedCells}: {updatedCells: Cells}) => void;
}): Promise<void> {
  if (gridIdsToFill.length > 0) {
    await withLoadingScreen(
      StorableActions.bulkFetchStorables(gridIdsToFill),
      'Calculating next available positions...'
    );
  }
  const gridsById = StorableStore.getGrids();

  const emptyPositionsByGridId = getEmptyPositionsByGridId({
    gridIds: gridIdsToFill,
    gridsById,
    orderingStrategy,
  });
  const columnConstraints = structuredTableAdapter.toTable(structuredTable).columnConstraints;
  const columnDefinitions = columnConstraints.map((constraint) => constraint!.columnDefinition!);
  const firstUnfinalizedIndex = structuredTable.rows.findIndex((row) => !row.isFinalized);

  const unfinalizedCells: Cells = structuredTable.rows.slice(firstUnfinalizedIndex).map((row) => row.cells);
  const unfinalizedComputedCells: Array<Array<EvaluatedCell>> = computedTable.slice(firstUnfinalizedIndex);
  const destinationColumnIdx = getDestinationBoxOrPlateColumnIndex({structuredTable, structuredTableAdapter});
  const destinationColumnDefinitionType = columnDefinitions[destinationColumnIdx].type;

  const newCells: Cells = fillNextEmptyPositions({
    cells: unfinalizedCells,
    computedTable: unfinalizedComputedCells,
    tableMetadata: structuredTable.metadata,
    fillableGridIds: gridIdsToFill,
    gridsById,
    availablePositionsByGridId: emptyPositionsByGridId,
    columnDefinitions,
    destinationColumnDefinitionType,
  });
  const updatedCells = structuredTable.rows.map((originalRow, idx) =>
    idx < firstUnfinalizedIndex ? originalRow.cells : newCells[idx - firstUnfinalizedIndex]
  );
  updateHandler({updatedCells});
}

function renderDropdownButton({
  structuredTable,
  structuredTableAdapter,
  computedTable,
  updateHandler,
}: ActionFunctionParameters<
  TransferStructuredTableMetadata,
  TransferStructuredTableRowMetadata
>): JSX.Element {
  const gridIdsToFill = getGridIdsToFill({structuredTable, structuredTableAdapter, computedTable});

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleFillingPositions = (orderingStrategy: OrderingStrategy) => {
    handleFillNextAvailablePositions({
      structuredTable,
      structuredTableAdapter,
      computedTable,
      orderingStrategy,
      gridIdsToFill,
      updateHandler,
    });
  };

  const destinationType = structuredTable.metadata.destinationType;
  const buttonStatus: ButtonStatus =
    gridIdsToFill.length > 0
      ? {
          tooltipTitle: getTooltipText({disabled: false, destinationType}),
          disabled: false,
        }
      : {
          tooltipTitle: getTooltipText({disabled: true, destinationType}),
          disabled: true,
        };

  return (
    <Tooltip title={buttonStatus.tooltipTitle}>
      <Dropdown
        menu={
          <Menu width={Menu.WIDTHS.SMALL} height={Menu.HEIGHTS.MEDIUM}>
            <Option
              icon="fill-across-rows"
              iconClassName="u-faded"
              value={OrderingStrategy.ROW_FIRST}
              onSelect={handleFillingPositions}
            >
              Across rows
            </Option>
            <Option
              icon="fill-down-columns"
              iconClassName="u-faded"
              value={OrderingStrategy.COLUMN_FIRST}
              onSelect={handleFillingPositions}
            >
              Down columns
            </Option>
          </Menu>
        }
      >
        <Button
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          className="u-mini-spacing-left"
          disabled={buttonStatus.disabled}
          icon="fill-grid"
          isDropdown={true}
          size={ButtonSize.MINI}
        />
      </Dropdown>
    </Tooltip>
  );
}

export const fillNextAvailablePositionsActionFunction = makeCustomButtonActionFunction({
  renderToolbarButton: renderDropdownButton,
  renderOverflowMenuItem: unsupportedOverflowMenuItem,
});
